<template>
    <div :class="alertClasses" role="alert">
        <div class="flex gap-2.5" :class="[center ? 'justify-center' : '', messages && messages.length ? 'items-start' : 'items-center']">
            <div class="flex-shrink-0" :class="{ 'mt-0.5': messages && messages.length }">
                <fa :icon="['fas', icon]" :spin="spin" :class="iconClasses" />
            </div>
            <div class="min-w-0" :class="{ 'flex-1': !center }">
                <p class="text-primary-100 text-sm">
                    <t :path="title" />
                </p>
                <ul v-if="messages && messages.length" class="text-primary-300 mt-1 space-y-0.5">
                    <li v-for="(message, idx) of messages" :key="idx" class="text-sm">• <t :path="message" /></li>
                </ul>
                <slot />
            </div>
            <div v-if="$slots.action" class="flex-shrink-0">
                <slot name="action" />
            </div>
            <button v-if="dismissible" @click="$emit('dismiss')" :class="dismissButtonClasses" type="button" aria-label="Dismiss">
                <fa :icon="['fas', 'times']" class="text-sm" />
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

export default defineComponent({
    emits: ['dismiss'],
    props: {
        type: {
            required: true,
            type: String,
            validator: (value: string) => ['info', 'danger', 'warning', 'success', 'primary', 'accent', 'inherit'].includes(value),
        },
        icon: {
            required: true,
            type: String,
        },
        spin: {
            type: Boolean,
            default: false,
        },
        title: {
            required: true,
            type: [String, Array],
        },
        messages: {
            type: Array,
        },
        dismissible: {
            type: Boolean,
            default: false,
        },
        center: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const alertClasses = computed(() => [
            'rounded border px-3 py-2',
            {
                'bg-info-500/10 border-info-500/20': props.type === 'info',
                'bg-danger-500/10 border-danger-500/20': props.type === 'danger',
                'bg-warning-500/10 border-warning-500/20': props.type === 'warning',
                'bg-success-500/10 border-success-500/20': props.type === 'success',
                'bg-primary-500/30 border-primary-400/10': props.type === 'primary',
                'bg-accent-500/10 border-accent-500/20': props.type === 'accent',
            },
        ]);

        const iconClasses = computed(() => [
            'text-sm',
            {
                'text-info-400': props.type === 'info',
                'text-danger-400': props.type === 'danger',
                'text-warning-400': props.type === 'warning',
                'text-success-400': props.type === 'success',
                'text-primary-400': props.type === 'primary',
                'text-accent-400': props.type === 'accent',
            },
        ]);

        const dismissButtonClasses = computed(() => [
            'flex-shrink-0 rounded p-1.5 transition-colors text-primary-300 hover:text-primary-100 hover:bg-primary-50/10 focus:outline-none focus:ring-2 focus:ring-white/20',
        ]);

        return {
            alertClasses,
            iconClasses,
            dismissButtonClasses,
        };
    },
});
</script>
